import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Grid,
  TextField,
  IconButton,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  Tooltip,
  Paper,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Send,
  Email,
  CheckCircle,
  Cancel,
  Schedule,
  Person,
  Business,
  Public,
  Article,
  Star,
  FilterList,
  Search,
  Download,
  Upload,
  Refresh,
  AccountBalance,
  Newspaper,
  TheaterComedy,
  AccountBalanceWallet,
  WorkspacePremium,
  RateReview,
  MusicNote,
  LocationCity,
  Groups
} from '@mui/icons-material';
import axiosinstance from '../../utils/axiosConfig';

interface InvitesPhaseProps {
  resource: any;
  onUpdate: (updatedResource: any) => void;
}

interface TargetedInvite {
  _id?: string;
  name: string;
  email: string;
  organization?: string;
  title?: string;
  category: string;
  notes?: string;
  invitationStatus: 'draft' | 'sent' | 'opened' | 'responded_yes' | 'responded_no' | 'responded_maybe' | 'attended';
  sentDate?: Date;
  responseDate?: Date;
  responseNotes?: string;
  customMessage?: string;
  emailTemplate?: string;
  followUpRequired?: boolean;
  followUpDate?: Date;
  followUpNotes?: string;
  masterListFlag?: boolean;
  addedBy?: string;
}

const InvitesPhase: React.FC<InvitesPhaseProps> = ({ resource, onUpdate }) => {
  const [invites, setInvites] = useState<TargetedInvite[]>(resource?.targetedInvites || []);
  const [filteredInvites, setFilteredInvites] = useState<TargetedInvite[]>(invites);
  const [selectedInvites, setSelectedInvites] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Dialog states
  const [addInviteDialogOpen, setAddInviteDialogOpen] = useState(false);
  const [emailComposerOpen, setEmailComposerOpen] = useState(false);
  
  // Form states
  const [newInvite, setNewInvite] = useState<TargetedInvite>({
    name: '',
    email: '',
    organization: '',
    title: '',
    category: 'other',
    notes: '',
    invitationStatus: 'draft',
    masterListFlag: false
  });
  
  const [emailData, setEmailData] = useState({
    template: 'standard',
    subject: '',
    content: '',
    customMessage: ''
  });
  
  const [saving, setSaving] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailError, setEmailError] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Categories for invites
  const inviteCategories = [
    { value: 'embassy', label: 'Embassy', icon: AccountBalance },
    { value: 'media', label: 'Media', icon: Newspaper },
    { value: 'arts_council', label: 'Arts Council', icon: TheaterComedy },
    { value: 'government', label: 'Government', icon: AccountBalance },
    { value: 'sponsors', label: 'Sponsors', icon: AccountBalanceWallet },
    { value: 'vips', label: 'VIPs', icon: WorkspacePremium },
    { value: 'critics', label: 'Critics', icon: RateReview },
    { value: 'industry', label: 'Industry', icon: MusicNote },
    { value: 'venue_staff', label: 'Venue Staff', icon: LocationCity },
    { value: 'other', label: 'Other', icon: Groups }
  ];

  // Status options - REALISTIC ONLY
  const statusOptions = [
    { value: 'draft', label: 'Draft', color: 'default' },
    { value: 'sent', label: 'Sent', color: 'info' },
    { value: 'confirmed_yes', label: 'Confirmed Yes', color: 'success' },
    { value: 'confirmed_no', label: 'Confirmed No', color: 'error' },
    { value: 'no_response', label: 'No Response', color: 'warning' }
  ];

  // Filter invites based on search and filters
  useEffect(() => {
    let filtered = invites;

    if (searchTerm) {
      filtered = filtered.filter(invite => 
        invite.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invite.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invite.organization?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterCategory !== 'all') {
      filtered = filtered.filter(invite => invite.category === filterCategory);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(invite => invite.invitationStatus === filterStatus);
    }

    setFilteredInvites(filtered);
  }, [invites, searchTerm, filterCategory, filterStatus]);

  // Calculate statistics - REALISTIC ONLY
  const getInviteStats = () => {
    const total = invites.length;
    const sent = invites.filter(inv => inv.invitationStatus !== 'draft').length;
    const attending = invites.filter(inv => inv.invitationStatus === 'responded_yes').length;
    
    return { total, sent, attending };
  };

  const inviteStats = getInviteStats();

  // Helper function to populate resource data with venue details
  const populateResourceDataForEmail = async (resourceData: any) => {
    if (!resourceData || !resourceData.performances || resourceData.performances.length === 0) {
      return resourceData;
    }

    try {
      // Create a copy to avoid mutating the original
      const populatedResourceData = { ...resourceData };
      
      // Populate each performance's venue data
      const populatedPerformances = await Promise.all(
        resourceData.performances.map(async (performance: any) => {
          if (performance.venue && typeof performance.venue === 'string') {
            // Venue is just an ID, fetch the full venue data
            try {
              const venueResponse = await axiosinstance.get(`/api/venues/${performance.venue}`);
              return {
                ...performance,
                venue: venueResponse.data
              };
            } catch (error) {
              console.error(`Failed to fetch venue ${performance.venue}:`, error);
              // Return original performance if venue fetch fails
              return performance;
            }
          } else {
            // Venue is already populated or doesn't exist
            return performance;
          }
        })
      );
      
      populatedResourceData.performances = populatedPerformances;
      return populatedResourceData;
      
    } catch (error) {
      console.error('Error populating resource data:', error);
      return resourceData; // Return original data if population fails
    }
  };

  // Get default email templates
  const getDefaultEmailContent = () => {
    const performanceName = resource?.performances?.[0]?.simpleBandName || 
                           resource?.performances?.[0]?.band?.name || 
                           resource?.name?.replace(/\s*-\s*(Resource Pack|Resources)$/i, '') || 
                           'Performance';
    
    return {
      subject: `Invitation: ${performanceName}`,
      content: `
You are cordially invited to attend our upcoming performance.

[Performance Details]

We hope you can join us for this special event! 
Please click the link below to RSVP.

Best regards,
Improvised Music Company`
    };
  };

  // Check for duplicate email
  const isDuplicateEmail = (email: string, excludeId?: string) => {
    return invites.some(invite => 
      invite.email.toLowerCase() === email.toLowerCase() && 
      invite._id !== excludeId
    );
  };

// Handle adding new invite with validation
const handleAddInvite = async () => {
  setEmailError('');

  // Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(newInvite.email)) {
    setEmailError('Please enter a valid email address');
    return;
  }

  // Check for duplicate email
  if (isDuplicateEmail(newInvite.email)) {
    setEmailError('This email address is already in your invite list');
    return;
  }

  try {
    // Add new invite WITHOUT _id - let MongoDB generate it
    const inviteWithoutId = { ...newInvite };
    delete inviteWithoutId._id; // Remove any existing _id

    const updatedInvites = [...invites, inviteWithoutId];
    
    // Save to database and get the response with generated _id
    setSaving(true);
    const updatedResource = { ...resource, targetedInvites: updatedInvites };
    
    const response = await axiosinstance.put(`/api/resources/${resource._id}`, updatedResource);
    
    // Update local state with the response data that includes MongoDB-generated _ids
    if (response.data && response.data.targetedInvites) {
      setInvites(response.data.targetedInvites);
      onUpdate(response.data);
    } else {
      // Fallback to original method if response doesn't include the data
      setInvites(updatedInvites);
      onUpdate(updatedResource);
    }
    
    // Reset form
    setNewInvite({
      name: '',
      email: '',
      organization: '',
      title: '',
      category: 'other',
      notes: '',
      invitationStatus: 'draft',
      masterListFlag: false
    });
    
    setAddInviteDialogOpen(false);
    
  } catch (error) {
    console.error('Error saving invite:', error);
    // Revert local state if save failed
    setInvites(invites);
  } finally {
    setSaving(false);
  }
};

  // Handle opening email composer with pre-populated content
  const handleOpenEmailComposer = () => {
    const defaultContent = getDefaultEmailContent();
    setEmailData({
      template: 'standard',
      subject: defaultContent.subject,
      content: defaultContent.content,
      customMessage: ''
    });
    setEmailComposerOpen(true);
  };

  // Handle editing invite
  const handleEditInvite = (inviteId: string, updatedData: Partial<TargetedInvite>) => {
    const updatedInvites = invites.map(invite => 
      invite._id === inviteId ? { ...invite, ...updatedData } : invite
    );
    setInvites(updatedInvites);
    saveInvitesToResource(updatedInvites);
  };

  // Handle deleting invite
  const handleDeleteInvite = (inviteId: string) => {
    const updatedInvites = invites.filter(invite => invite._id !== inviteId);
    setInvites(updatedInvites);
    saveInvitesToResource(updatedInvites);
  };

  // Handle test email sending - FIXED WITH PROPER TEMPLATE
  const handleSendTestEmail = async () => {
    setSendingEmails(true);
    try {
      const testInvites: TargetedInvite[] = [{
        name: 'Test User',
        email: 'ken@improvisedmusic.ie',
        organization: 'Test Organization',
        category: 'other',
        invitationStatus: 'draft'
      }];

      // Use the same template system as bulk emails
      const defaultContent = getDefaultEmailContent();

      // Populate venue data before sending
      const populatedResourceData = await populateResourceDataForEmail(resource);

      const response = await axiosinstance.post('/api/email/invitations', {
        invites: testInvites,
        emailTemplate: 'standard',
        subject: `[TEST] ${defaultContent.subject}`,
        content: defaultContent.content,
        resourceData: populatedResourceData,
        testMode: true
      });

      if (response.data.success) {
        console.log('Test email sent successfully:', response.data);
      }
    } catch (error) {
      console.error('Error sending test email:', error);
    } finally {
      setSendingEmails(false);
    }
  };

  // Handle bulk email sending - FIXED WITH VENUE POPULATION
  const handleSendBulkEmails = async () => {
    setSendingEmails(true);
    try {
      const invitesToSend = invites.filter(invite => selectedInvites.includes(invite._id!));
      
      // Populate venue data before sending
      const populatedResourceData = await populateResourceDataForEmail(resource);
      
      const response = await axiosinstance.post('/api/email/invitations', {
        invites: invitesToSend,
        emailTemplate: emailData.template,
        subject: emailData.subject,
        content: emailData.content,
        resourceData: populatedResourceData,
        testMode: false
      });

      if (response.data.success) {
        const updatedInvites = invites.map(invite => {
          const sentResult = response.data.results.find((r: any) => r.email === invite.email && r.success);
          if (sentResult) {
            return { ...invite, invitationStatus: 'sent' as const, sentDate: new Date() };
          }
          return invite;
        });
        
        setInvites(updatedInvites);
        saveInvitesToResource(updatedInvites);
        
        console.log(`Emails sent successfully: ${response.data.summary.successful} sent, ${response.data.summary.failed} failed`);
      }
      
      setSelectedInvites([]);
      setEmailComposerOpen(false);
    } catch (error) {
      console.error('Error sending emails:', error);
    } finally {
      setSendingEmails(false);
    }
  };

  // Save invites to resource
  const saveInvitesToResource = async (updatedInvites: TargetedInvite[]) => {
    setSaving(true);
    try {
      const updatedResource = { ...resource, targetedInvites: updatedInvites };
      
      await axiosinstance.put(`/api/resources/${resource._id}`, updatedResource);
      onUpdate(updatedResource);
    } catch (error) {
      console.error('Error saving invites:', error);
    } finally {
      setSaving(false);
    }
  };

  // Get category info
  const getCategoryInfo = (category: string) => {
    return inviteCategories.find(cat => cat.value === category) || inviteCategories.find(cat => cat.value === 'other')!;
  };

  // Get status info
  const getStatusInfo = (status: string) => {
    return statusOptions.find(opt => opt.value === status) || statusOptions[0];
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {/* Header with Statistics */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Targeted Invitations
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Manage embassy, media, and VIP invitations for this performance
        </Typography>

        {/* Quick Stats - REALISTIC ONLY */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="primary">{inviteStats.total}</Typography>
              <Typography variant="caption">Total Invites</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">{inviteStats.sent}</Typography>
              <Typography variant="caption">Emails Sent</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">{inviteStats.attending}</Typography>
              <Typography variant="caption">Confirmed Attending</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
          <Tab label={`All Invites (${invites.length})`} />
          <Tab label="Email Campaigns" />
        </Tabs>
      </Card>

      {/* All Invites Tab */}
      {activeTab === 0 && (
        <>
          {/* Filters and Actions */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search invites..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      label="Category"
                    >
                      <MenuItem value="all">All Categories</MenuItem>
                      {inviteCategories.map(cat => (
                        <MenuItem key={cat.value} value={cat.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <cat.icon fontSize="small" />
                            {cat.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      {statusOptions.map(status => (
                        <MenuItem key={status.value} value={status.value}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => setAddInviteDialogOpen(true)}
                      size="small"
                    >
                      Add Invite
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Email />}
                      onClick={handleOpenEmailComposer}
                      disabled={selectedInvites.length === 0}
                      size="small"
                    >
                      Email ({selectedInvites.length})
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Send />}
                      onClick={handleSendTestEmail}
                      size="small"
                      color="secondary"
                      disabled={sendingEmails}
                    >
                      Test Email
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Invites List */}
          <Card>
            <List>
              {filteredInvites.map((invite, index) => {
                const categoryInfo = getCategoryInfo(invite.category);
                const statusInfo = getStatusInfo(invite.invitationStatus);
                
                return (
                  <React.Fragment key={invite._id || index}>
                    <ListItem>
                      <Checkbox
                        checked={invite._id ? selectedInvites.includes(invite._id) : false}
                        onChange={(e) => {
                          if (!invite._id) return; // Skip invites without proper IDs
                          if (e.target.checked) {
                            setSelectedInvites([...selectedInvites, invite._id]);
                          } else {
                            setSelectedInvites(selectedInvites.filter(id => id !== invite._id));
                          }
                        }}
                        sx={{ mr: 1 }}
                      />
                      
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <categoryInfo.icon fontSize="small" />
                        </Avatar>
                      </ListItemAvatar>
                      
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1">{invite.name}</Typography>
                            <Chip 
                              label={statusInfo.label} 
                              color={statusInfo.color as any} 
                              size="small" 
                            />
                            {invite.masterListFlag && (
                              <Tooltip title="Added to master list">
                                <Star color="warning" fontSize="small" />
                              </Tooltip>
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {invite.email} • {categoryInfo.label}
                            </Typography>
                            {invite.organization && (
                              <Typography variant="caption" color="text.secondary">
                                {invite.organization}
                                {invite.title && ` • ${invite.title}`}
                              </Typography>
                            )}
                            {invite.notes && (
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                {invite.notes}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      
                      <ListItemSecondaryAction>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {/* Quick status toggle for sent invites */}
                          {invite.invitationStatus !== 'draft' && (
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={invite.invitationStatus}
                                onChange={(e) => handleEditInvite(invite._id!, { invitationStatus: e.target.value as any })}
                                variant="outlined"
                                size="small"
                              >
                                <MenuItem value="sent">Sent</MenuItem>
                                <MenuItem value="confirmed_yes">✓ Confirmed Yes</MenuItem>
                                <MenuItem value="confirmed_no">✗ Confirmed No</MenuItem>
                                <MenuItem value="no_response">No Response</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                          
                          <IconButton 
                            size="small"
                            onClick={() => {
                              console.log('Edit invite:', invite);
                            }}
                            disabled={!invite._id}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => invite._id && handleDeleteInvite(invite._id)}
                            disabled={!invite._id}
                          >
                            <Delete />
                          </IconButton>
                        </Stack>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < filteredInvites.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
            
            {filteredInvites.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Person sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No invites found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {invites.length === 0 ? 'Get started by adding your first invite' : 'Try adjusting your filters'}
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<Add />}
                  onClick={() => setAddInviteDialogOpen(true)}
                >
                  Add First Invite
                </Button>
              </Box>
            )}
          </Card>
        </>
      )}

      {/* Email Campaigns Tab */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Email Campaign Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Send targeted emails to specific groups of invitees
            </Typography>
            
            <Grid container spacing={3}>
              {inviteCategories.map(category => {
                const categoryInvites = invites.filter(inv => inv.category === category.value);
                const sentCount = categoryInvites.filter(inv => inv.invitationStatus !== 'draft').length;
                
                return (
                  <Grid item xs={12} sm={6} md={4} key={category.value}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <category.icon color="primary" sx={{ mr: 1 }} />
                          <Typography variant="subtitle1">
                            {category.label}
                          </Typography>
                        </Box>
                        
                        <Typography variant="h4" color="primary" gutterBottom>
                          {categoryInvites.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {sentCount} invitations sent
                        </Typography>
                        
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Email />}
                          disabled={categoryInvites.length === 0}
                          onClick={() => {
                            setSelectedInvites(categoryInvites.map(inv => inv._id!));
                            handleOpenEmailComposer();
                          }}
                        >
                          Email All
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Add Invite Dialog - WITH VALIDATION */}
      <Dialog open={addInviteDialogOpen} onClose={() => setAddInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Invite</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Name"
              value={newInvite.name}
              onChange={(e) => setNewInvite(prev => ({ ...prev, name: e.target.value }))}
              required
            />
            
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={newInvite.email}
              onChange={(e) => {
                setNewInvite(prev => ({ ...prev, email: e.target.value }));
                setEmailError(''); // Clear error when typing
              }}
              required
              error={!!emailError}
              helperText={emailError}
            />
            
            <TextField
              fullWidth
              label="Organization"
              value={newInvite.organization}
              onChange={(e) => setNewInvite(prev => ({ ...prev, organization: e.target.value }))}
            />
            
            <TextField
              fullWidth
              label="Title"
              value={newInvite.title}
              onChange={(e) => setNewInvite(prev => ({ ...prev, title: e.target.value }))}
            />
            
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={newInvite.category}
                onChange={(e) => setNewInvite(prev => ({ ...prev, category: e.target.value }))}
                label="Category"
              >
                {inviteCategories.map(cat => (
                  <MenuItem key={cat.value} value={cat.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <cat.icon fontSize="small" />
                      {cat.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={newInvite.notes}
              onChange={(e) => setNewInvite(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Special requirements, preferences, etc."
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={newInvite.masterListFlag}
                  onChange={(e) => setNewInvite(prev => ({ ...prev, masterListFlag: e.target.checked }))}
                />
              }
              label="Add to master invite list for future events"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddInviteDialogOpen(false);
            setEmailError('');
          }}>Cancel</Button>
          <Button 
            onClick={handleAddInvite} 
            variant="contained"
            disabled={!newInvite.name || !newInvite.email}
          >
            Add Invite
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Composer Dialog - PRE-POPULATED */}
      <Dialog open={emailComposerOpen} onClose={() => setEmailComposerOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Send Invitations ({selectedInvites.length} recipients)
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Subject"
              value={emailData.subject}
              onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
            />
            
            <TextField
              fullWidth
              label="Email Content"
              multiline
              rows={8}
              value={emailData.content}
              onChange={(e) => setEmailData(prev => ({ ...prev, content: e.target.value }))}
            />
            
            <Alert severity="info">
              <Typography variant="body2">
                You can use placeholders like [Name], [Organization], and [Performance Details] which will be automatically replaced for each recipient.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailComposerOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSendBulkEmails} 
            variant="contained"
            disabled={sendingEmails || !emailData.subject || !emailData.content}
            startIcon={sendingEmails ? <LinearProgress /> : <Send />}
          >
            {sendingEmails ? 'Sending...' : 'Send Invitations'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save indicator */}
      {saving && (
        <Box sx={{ position: 'fixed', bottom: 16, right: 16 }}>
          <Alert severity="info" sx={{ alignItems: 'center' }}>
            <LinearProgress sx={{ width: 100, mr: 1 }} />
            Saving invites...
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default InvitesPhase;